import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import { BASE_APP_TITLE, isDevEnvironment, DEV_TITLE_SUFFIX } from '../utils/environment';

export default function PublicOrderPayment({ language, onLanguageChange, t }) {
    const { orderId } = useParams();
    const [searchParams] = useSearchParams();

    const [order, setOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPaying, setIsPaying] = useState(false);
    const [showDetails, setShowDetails] = useState(false);

    const isPaymentSuccess = searchParams.get('payment') === 'success';

    const apiUrl = process.env.REACT_APP_API_URL || '';

    const fetchOrderDetails = useCallback(async () => {
        if (!orderId) {
            setError('no_order_id');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${apiUrl}/api/public/orders/${encodeURIComponent(orderId)}`);
            if (response.status === 404) {
                setError('not_found');
                setOrder(null);
            } else if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                setError(data.error || 'fetch_failed');
                setOrder(null);
            } else {
                const data = await response.json();
                setOrder(data);
            }
        } catch (err) {
            console.error('Failed to load public order:', err);
            setError(err.message || 'network_error');
        } finally {
            setIsLoading(false);
        }
    }, [apiUrl, orderId]);

    useEffect(() => {
        fetchOrderDetails();
    }, [fetchOrderDetails]);

    // Handle payment redirect params from Stripe
    useEffect(() => {
        const paymentStatus = searchParams.get('payment');
        if (paymentStatus === 'success') {
            toast.success(t.payment_successful || 'Payment successful!');
            // Refetch order after a short delay so webhook update is reflected
            const timer = setTimeout(() => {
                fetchOrderDetails();
            }, 1500);
            return () => clearTimeout(timer);
        } else if (paymentStatus === 'cancelled') {
            toast.error(t.payment_cancelled || 'Payment cancelled.');
        }
    }, [searchParams, t, fetchOrderDetails]);

    const handlePayWithStripe = async () => {
        if (!orderId || isPaying) return;
        setIsPaying(true);

        try {
            const response = await fetch(`${apiUrl}/api/public/orders/${encodeURIComponent(orderId)}/pay`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            if (response.ok && data.url) {
                window.location.href = data.url;
            } else {
                toast.error(data.error || 'Failed to initialize payment.');
                setIsPaying(false);
            }
        } catch (err) {
            console.error('Error starting Stripe checkout:', err);
            toast.error(err.message || 'An unexpected error occurred.');
            setIsPaying(false);
        }
    };

    const getStatusBadge = (status, isPaid) => {
        if (isPaid || status?.includes('Completed') || status === 'Paid') {
            return (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                    <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                    </svg>
                    {t.order_already_paid ? 'Betaald' : 'Paid'}
                </span>
            );
        }
        if (status === 'Failed') {
            return (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-200">
                    <svg className="w-3.5 h-3.5 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {status}
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                <svg className="w-3.5 h-3.5 text-amber-600 animate-pulse" fill="currentColor" viewBox="0 0 8 8">
                    <circle cx="4" cy="4" r="3" />
                </svg>
                {status || (t.pending || 'Pending Payment')}
            </span>
        );
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col justify-between text-slate-800">
            <Toaster position="top-right" />

            {/* Public Header */}
            <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-30">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src="/xebia-logo.png" alt="Xebia Logo" className="h-8 sm:h-9 object-contain" />
                        <span className="text-slate-300 font-light text-xl">|</span>
                        <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900 tracking-tight text-sm sm:text-base">
                                {BASE_APP_TITLE}
                            </span>
                            {isDevEnvironment() && (
                                <span className="px-2 py-0.5 rounded border border-amber-300 bg-amber-100 text-amber-900 text-[10px] font-semibold">
                                    {DEV_TITLE_SUFFIX}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Language Selector */}
                        <div className="flex items-center bg-slate-100 rounded-lg p-0.5 text-xs font-medium">
                            <button
                                type="button"
                                onClick={() => onLanguageChange('nl')}
                                className={`px-2.5 py-1 rounded-md transition-all ${
                                    language === 'nl'
                                        ? 'bg-white text-slate-900 shadow-sm font-semibold'
                                        : 'text-slate-600 hover:text-slate-900'
                                }`}
                            >
                                NL
                            </button>
                            <button
                                type="button"
                                onClick={() => onLanguageChange('en')}
                                className={`px-2.5 py-1 rounded-md transition-all ${
                                    language === 'en'
                                        ? 'bg-white text-slate-900 shadow-sm font-semibold'
                                        : 'text-slate-600 hover:text-slate-900'
                                }`}
                            >
                                EN
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content Area */}
            <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-8 sm:py-12">
                {isLoading ? (
                    <div className="bg-white rounded-2xl p-12 border border-slate-200 shadow-sm text-center">
                        <div className="inline-flex items-center justify-center p-3 bg-indigo-50 rounded-full mb-4">
                            <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        <h2 className="text-lg font-semibold text-slate-900">{t.loading || 'Loading order details...'}</h2>
                        <p className="text-sm text-slate-500 mt-1">{t.paying || 'Please wait a moment.'}</p>
                    </div>
                ) : error === 'not_found' ? (
                    <div className="bg-white rounded-2xl p-10 border border-slate-200 shadow-sm text-center max-w-lg mx-auto">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-rose-50 text-rose-500 rounded-full mb-4">
                            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-bold text-slate-900">{t.order_not_found || 'Order Not Found'}</h2>
                        <p className="text-sm text-slate-600 mt-2">
                            {t.order_not_found_desc || 'The requested invoice or order could not be found. Please check the URL or contact support.'}
                        </p>
                        <div className="mt-6 pt-6 border-t border-slate-100 flex justify-center">
                            <a
                                href="mailto:workplace.support@xebia.com"
                                className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-800"
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                workplace.support@xebia.com
                            </a>
                        </div>
                    </div>
                ) : error ? (
                    <div className="bg-white rounded-2xl p-8 border border-rose-200 shadow-sm text-center max-w-lg mx-auto">
                        <p className="text-rose-600 font-semibold mb-2">Error</p>
                        <p className="text-sm text-slate-600">{error}</p>
                        <button
                            onClick={fetchOrderDetails}
                            className="mt-4 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition"
                        >
                            Retry
                        </button>
                    </div>
                ) : isPaymentSuccess ? (
                    <div className="space-y-6">
                        {/* Thank you card */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-12 text-center">
                            <div className="w-20 h-20 bg-emerald-50 border-4 border-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-sm">
                                <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-5">
                                {t.thank_you_payment_for || 'Thank you for the payment for'} <span className="text-indigo-600 font-mono">{order?.orderId || orderId}</span>
                            </h1>
                            <p className="text-base text-slate-600 mt-3 max-w-xl mx-auto">
                                {t.payment_success_desc || 'Your payment has been successfully received. Your Google Workspace licenses and services are being automatically updated and reactivated.'}
                            </p>

                            {/* Summary receipt info */}
                            {order && (
                                <div className="mt-8 max-w-lg mx-auto bg-slate-50 border border-slate-200 rounded-xl p-5 text-left text-sm space-y-3">
                                    <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                                        <span className="text-slate-500 font-medium">{t.payment_reference || 'Invoice Number'}</span>
                                        <span className="font-mono font-semibold text-slate-800">{order.orderId}</span>
                                    </div>
                                    {order.customerName && (
                                        <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                                            <span className="text-slate-500 font-medium">{t.customer || 'Customer'}</span>
                                            <span className="font-semibold text-slate-800">{order.customerName}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                                        <span className="text-slate-500 font-medium">{t.status || 'Status'}</span>
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                                            <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                            </svg>
                                            {t.status_completed || 'Betaald'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center pt-1 text-base font-bold text-slate-900">
                                        <span>{t.amount_paid || 'Amount Paid'}</span>
                                        <span className="font-mono text-emerald-700 text-lg">€ {order.totalPrice?.toFixed(2)}</span>
                                    </div>
                                </div>
                            )}

                            {/* PDF Invoice link if available */}
                            {order?.invoicePdfLink && (
                                <div className="mt-6 flex justify-center">
                                    <a
                                        href={order.invoicePdfLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-xl hover:bg-slate-800 transition shadow-sm"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        {t.download_invoice_pdf || 'Download Invoice (PDF)'}
                                    </a>
                                </div>
                            )}

                            {/* Collapsible toggle to view items if desired */}
                            {order?.cartItems && order.cartItems.length > 0 && (
                                <div className="mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setShowDetails(!showDetails)}
                                        className="text-xs font-semibold text-slate-500 hover:text-slate-800 inline-flex items-center gap-1.5 transition"
                                    >
                                        <span>{showDetails ? (t.hide_details || 'Hide details') : (t.show_details || 'View purchased items')}</span>
                                        <svg className={`w-3.5 h-3.5 transform transition-transform ${showDetails ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>

                                    {showDetails && (
                                        <div className="mt-4 max-w-lg mx-auto bg-slate-50 border border-slate-200 rounded-xl p-4 text-left divide-y divide-slate-200">
                                            {order.cartItems.map((item, idx) => (
                                                <div key={idx} className="py-2.5 first:pt-0 last:pb-0 flex justify-between items-start gap-4">
                                                    <div>
                                                        <div className="text-sm font-medium text-slate-900">{item.name}</div>
                                                        {item.item_subtitle && (
                                                            <div className="text-xs text-slate-500 mt-0.5">{item.item_subtitle}</div>
                                                        )}
                                                        <div className="text-xs text-slate-400 mt-0.5">{t.item_quantity || 'Qty'}: {item.quantity} × € {item.price?.toFixed(2)}</div>
                                                    </div>
                                                    <div className="text-sm font-semibold font-mono text-slate-800">
                                                        € {item.subtotal?.toFixed(2)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Customer Support Footer Card */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-600">
                            <div>
                                <span className="font-semibold text-slate-800">{t.need_help || 'Need help?'} </span>
                                <span>{language === 'nl' ? 'Heeft u vragen over deze bestelling of licenties?' : 'Questions about this invoice or licenses?'}</span>
                            </div>
                            <a
                                href="mailto:workplace.support@xebia.com"
                                className="font-medium text-indigo-600 hover:text-indigo-800 transition"
                            >
                                {t.contact_support || 'Contact support'}: workplace.support@xebia.com
                            </a>
                        </div>
                    </div>
                ) : order && (
                    <div className="space-y-6">
                        {/* Notice for suspended users */}
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 sm:p-5 flex items-start gap-3.5">
                            <div className="p-1 bg-amber-100 rounded-lg text-amber-700 shrink-0 mt-0.5">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div className="text-sm text-amber-900 leading-relaxed">
                                <span className="font-semibold block mb-0.5">
                                    {language === 'nl' ? 'Account Opschorting Betaling' : 'Account Reactivation Payment'}
                                </span>
                                {t.suspended_workspace_notice}
                            </div>
                        </div>

                        {/* Invoice & Order Card */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                            {/* Card Header */}
                            <div className="p-6 sm:p-8 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gradient-to-b from-slate-50/50 to-white">
                                <div>
                                    <div className="flex items-center gap-3">
                                        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
                                            {t.invoice_for_order || 'Invoice for Order'} {order.orderId}
                                        </h1>
                                        {getStatusBadge(order.status, order.isPaid)}
                                    </div>
                                    <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-slate-500">
                                        {order.createdAt && (
                                            <div>
                                                <span className="font-medium text-slate-700">{t.date || 'Date'}: </span>
                                                {new Date(order.createdAt).toLocaleDateString(language === 'nl' ? 'nl-NL' : 'en-US', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </div>
                                        )}
                                        {order.customerName && (
                                            <div>
                                                <span className="font-medium text-slate-700">{t.customer || 'Customer'}: </span>
                                                {order.customerName}
                                            </div>
                                        )}
                                        {order.orderUser && (
                                            <div>
                                                <span className="font-medium text-slate-700">{t.ordered_by || 'Ordered by'}: </span>
                                                {order.orderUser}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {order.isPaid && order.invoicePdfLink && (
                                    <div>
                                        <a
                                            href={order.invoicePdfLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition shadow-sm"
                                        >
                                            <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            {t.download_invoice_pdf || 'Download Invoice (PDF)'}
                                        </a>
                                    </div>
                                )}
                            </div>

                            {/* Line Items Table */}
                            <div className="p-6 sm:p-8">
                                <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4">
                                    {t.order_details || 'Order Details'}
                                </h3>

                                <div className="overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead>
                                            <tr className="border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                                <th className="pb-3 pr-4">{t.item_name || 'Name'}</th>
                                                <th className="pb-3 pr-4">{t.item_description || 'Description'}</th>
                                                <th className="pb-3 pr-4 text-right">{t.item_price || 'Price (€)'}</th>
                                                <th className="pb-3 pr-4 text-right">{t.item_quantity || 'Quantity'}</th>
                                                <th className="pb-3 text-right">{t.item_subtotal || 'Subtotal'}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {order.cartItems && order.cartItems.length > 0 ? (
                                                order.cartItems.map((item, idx) => (
                                                    <tr key={idx} className="text-sm text-slate-700">
                                                        <td className="py-3.5 pr-4 font-medium text-slate-900">
                                                            <div>{item.name}</div>
                                                            {item.item_subtitle && (
                                                                <div className="text-xs text-slate-500 font-normal mt-0.5">
                                                                    {item.item_subtitle}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="py-3.5 pr-4 text-slate-500">
                                                            {item.description || '-'}
                                                        </td>
                                                        <td className="py-3.5 pr-4 text-right font-mono">
                                                            € {item.price?.toFixed(2)}
                                                        </td>
                                                        <td className="py-3.5 pr-4 text-right">
                                                            {item.quantity}
                                                        </td>
                                                        <td className="py-3.5 text-right font-mono font-medium text-slate-900">
                                                            € {item.subtotal?.toFixed(2)}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={5} className="py-6 text-center text-slate-400 text-sm">
                                                        {t.no_items || 'No items listed.'}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Financial Summary */}
                                <div className="mt-8 pt-6 border-t border-slate-200 flex justify-end">
                                    <div className="w-full max-w-xs space-y-2.5">
                                        <div className="flex justify-between text-sm text-slate-600">
                                            <span>{t.subtotal || 'Subtotal'}</span>
                                            <span className="font-mono">€ {order.subtotal?.toFixed(2)}</span>
                                        </div>
                                        {order.paypalFee > 0 && (
                                            <div className="flex justify-between text-sm text-slate-600">
                                                <span>{t.paypal_fee || 'Fee'}</span>
                                                <span className="font-mono">€ {order.paypalFee?.toFixed(2)}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between text-sm text-slate-600">
                                            <span>{t.vat_amount || 'VAT (21%)'}</span>
                                            <span className="font-mono">€ {order.vatAmount?.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-base font-bold text-slate-900 border-t border-slate-200 pt-3">
                                            <span>{t.total || 'Total'}</span>
                                            <span className="font-mono text-lg text-indigo-700">
                                                € {order.totalPrice?.toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Payment Action Bar */}
                                <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                                    <div className="text-xs text-slate-500 text-center sm:text-left flex items-center gap-2">
                                        <svg className="w-4 h-4 text-emerald-600 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
                                        </svg>
                                        <span>{t.secure_payment_stripe_desc}</span>
                                    </div>

                                    <div>
                                        {order.isPaid ? (
                                            <div className="flex items-center gap-2 px-5 py-2.5 bg-emerald-50 text-emerald-800 rounded-xl text-sm font-semibold border border-emerald-200">
                                                <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span>{t.order_already_paid || 'Order is already paid'}</span>
                                            </div>
                                        ) : order.isPayable ? (
                                            <button
                                                type="button"
                                                onClick={handlePayWithStripe}
                                                disabled={isPaying}
                                                className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-6 py-3 rounded-xl text-white font-semibold text-sm transition-all shadow-md hover:shadow-lg disabled:opacity-60 bg-[#635BFF] hover:bg-[#5349e4] active:scale-[0.98]"
                                            >
                                                {isPaying ? (
                                                    <>
                                                        <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        <span>{t.paying || 'Processing...'}</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                                                            <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697.4 12.51.4 6.273.4 2.2 3.68 2.2 8.788c0 5.49 4.887 6.476 8.358 7.747 2.457.9 3.297 1.644 3.297 2.651 0 1.055-.992 1.636-2.58 1.636-2.482 0-5.385-1.127-7.433-2.282l-.936 5.613c2.083 1.077 5.253 1.747 8.369 1.747 6.643 0 10.742-3.134 10.742-8.529 0-4.884-3.524-6.883-8.041-8.221z" />
                                                        </svg>
                                                        <span>{t.pay_with_stripe || 'Pay with Stripe'}</span>
                                                    </>
                                                )}
                                            </button>
                                        ) : (
                                            <span className="text-sm text-slate-500 font-medium">
                                                Status: {order.status}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Customer Support Footer Card */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-600">
                            <div>
                                <span className="font-semibold text-slate-800">{t.need_help || 'Need help?'} </span>
                                <span>{language === 'nl' ? 'Heeft u vragen over deze bestelling of licenties?' : 'Questions about this invoice or licenses?'}</span>
                            </div>
                            <a
                                href="mailto:workplace.support@xebia.com"
                                className="font-medium text-indigo-600 hover:text-indigo-800 transition"
                            >
                                {t.contact_support || 'Contact support'}: workplace.support@xebia.com
                            </a>
                        </div>
                    </div>
                )}
            </main>

            {/* Footer */}
            <footer className="bg-white border-t border-slate-200 py-6 text-center text-xs text-slate-400">
                <p>&copy; {new Date().getFullYear()} Xebia. All rights reserved.</p>
            </footer>
        </div>
    );
}
